import type { DefaultContext, Regl } from "regl";

const config = {
  backgroundColor: [0.1364, 0.31, 0.4211],
  cloudColor: [0.02, 0.05, 0.06],
  cloudScale: 0.5,
  cloudDriftSpeed: 0.1,
  cloudContrast: 0.2,
};

function supportsWebGL(): boolean {
  const canvas = document.createElement("canvas");
  const gl =
    canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
  return gl !== null;
}

function getCanvas(): HTMLCanvasElement | null {
  const canvas = document.getElementById("canvas");
  return canvas instanceof HTMLCanvasElement ? canvas : null;
}

function getViewport(): VisualViewport {
  if (!window.visualViewport) {
    throw new Error("VisualViewport is not supported");
  }
  return window.visualViewport;
}

function setupCanvas(
  canvas: HTMLCanvasElement,
  viewport: VisualViewport,
): void {
  canvas.width = viewport.width * window.devicePixelRatio;
  canvas.height = viewport.height * window.devicePixelRatio;
}

function createDrawBackground(
  regl: Regl,
  vert: string,
  frag: string,
): () => void {
  return regl({
    vert,
    frag,
    attributes: {
      position: [-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1],
    },
    uniforms: {
      time: (ctx: DefaultContext) => ctx.time,
      resolution: (ctx: DefaultContext) => [
        ctx.viewportWidth,
        ctx.viewportHeight,
      ],
      dpr: () => window.devicePixelRatio,
      backgroundColor: config.backgroundColor,
      cloudColor: config.cloudColor,
      cloudScale: config.cloudScale,
      cloudDriftSpeed: config.cloudDriftSpeed,
      cloudContrast: config.cloudContrast,
    },
    count: 6,
  });
}

async function init(): Promise<void> {
  const canvas = getCanvas();
  if (!canvas) {
    return;
  }

  if (!supportsWebGL()) {
    return;
  }

  const [{ default: createRegl }, { shader: vert }, { shader: frag }] =
    await Promise.all([
      import("regl"),
      import("./shaders/background.vert"),
      import("./shaders/background.frag"),
    ]);

  const viewport = getViewport();
  setupCanvas(canvas, viewport);

  const regl = createRegl({
    canvas,
    attributes: { antialias: true, alpha: false },
  });

  const drawBackground = createDrawBackground(regl, vert, frag);

  const handleResize = (): void => {
    setupCanvas(canvas, viewport);
    regl.poll();
  };

  window.addEventListener("resize", handleResize);
  viewport.addEventListener("resize", handleResize);

  let frameHandle = regl.frame(() => {
    regl.clear({ color: [0, 0, 0, 1] });
    drawBackground();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      frameHandle.cancel();
    } else {
      regl.poll();
      frameHandle = regl.frame(() => {
        regl.clear({ color: [0, 0, 0, 1] });
        drawBackground();
      });
    }
  });
}

void init();
